import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { User } from 'types/user';
import { Menu, X } from 'lucide-react';

interface NavbarProps {
    user: User | null;
    onLogout: () => void;
    onOpenAuth: () => void;
}

export const Navbar = ({ user, onLogout, onOpenAuth }: NavbarProps) => {
    const { t, i18n } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleLanguage = () => {
        const newLang = i18n.language === 'fr' ? 'en' : 'fr';
        i18n.changeLanguage(newLang);
    };

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setIsMobileMenuOpen(false);
        }
    };

    const NavLinks = () => (
        <>
            <button onClick={() => scrollToSection('value-props')} className="text-gray-600 hover:text-primary font-medium transition-colors">
                {t('navbar.benefits')}
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="text-gray-600 hover:text-primary font-medium transition-colors">
                {t('navbar.howItWorks')}
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="text-gray-600 hover:text-primary font-medium transition-colors">
                {t('navbar.testimonials')}
            </button>
            <button onClick={() => scrollToSection('universities')} className="text-gray-600 hover:text-primary font-medium transition-colors">
                {t('navbar.universities')}
            </button>
        </>
    );

    return (
        <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 transition-all duration-300">
            <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                <div className="flex items-center gap-8">
                    <img
                        src="/yapadra-logo.png"
                        alt="Yapadra Health"
                        className="h-10 md:h-12 cursor-pointer"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    />
                    <div className="hidden md:flex items-center gap-6">
                        <NavLinks />
                    </div>
                </div>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center gap-4">
                    {user ? (
                        <>
                            <Button
                                variant="ghost"
                                onClick={toggleLanguage}
                                className="mr-2 font-medium"
                            >
                                {i18n.language === 'fr' ? 'EN' : 'FR'}
                            </Button>
                            <span className="text-gray-600">
                                {t('navbar.welcome')} <span className="font-medium text-primary">{user.firstName}</span>
                            </span>
                            <Button
                                onClick={onLogout}
                                variant="outline"
                                className="transform transition-transform duration-300 hover:scale-105"
                            >
                                {t('navbar.logout')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="ghost"
                                onClick={toggleLanguage}
                                className="mr-2 font-medium"
                            >
                                {i18n.language === 'fr' ? 'EN' : 'FR'}
                            </Button>
                            <Button
                                onClick={onOpenAuth}
                                className="bg-primary text-white hover:bg-primary/90 transform transition-transform duration-300 hover:scale-105 shadow-md hover:shadow-lg"
                            >
                                {t('navbar.login')}
                            </Button>
                        </>
                    )}
                </div>

                {/* Mobile Menu - Language Toggle + Hamburger */}
                <div className="md:hidden flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleLanguage}
                        className="text-gray-600 text-lg"
                        title={i18n.language === 'fr' ? 'Switch to English' : 'Passer en Français'}
                    >
                        {i18n.language === 'fr' ? '🇬🇧' : '🇫🇷'}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="text-gray-600"
                    >
                        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </Button>
                </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
                <div className="md:hidden bg-white border-t border-gray-100 absolute w-full shadow-lg animate-slideDown">
                    <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
                        <div className="flex flex-col gap-4 border-b border-gray-100 pb-4">
                            <NavLinks />
                        </div>
                        {user ? (
                            <>
                                <div className="text-gray-600 py-2">
                                    {t('navbar.welcome')} <span className="font-medium text-primary">{user.firstName}</span>
                                </div>
                                <Button
                                    onClick={() => {
                                        onLogout();
                                        setIsMobileMenuOpen(false);
                                    }}
                                    variant="outline"
                                    className="w-full"
                                >
                                    {t('navbar.logout')}
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={() => {
                                    onOpenAuth();
                                    setIsMobileMenuOpen(false);
                                }}
                                className="bg-primary text-white hover:bg-primary/90 w-full shadow-md"
                            >
                                {t('navbar.login')}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};
